import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, Box, Tabs, Tab, Alert, useTheme } from "@mui/material";
import EligibleChatUsers from "./EligibleChatUsers";
import ChatHeader from "./ChatHeader";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";
import {
  fetchConversations,
  fetchEligibleChatUsers,
  getOrCreateConversation,
  addMessage,
  markAsRead
} from "../features/chatRedux/chatAction";
import { removeFavouration } from "../features/auth/authAction";
import { addMessageToConversation, clearChatError, setOnlineUsers, updateOnlineStatus, incrementUnreadCount, resetUnreadCount } from "../features/chatRedux/chatSlice";
import { socket, connectWithToken } from "./socket";
import { encrypt, decrypt } from "../utils/encryption";
import getUserToken from "../helpers/tokenValidation";


const ChatPopup = ({ open, onClose, currentUserId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  // Safe selector with defaults
  const chatState = useSelector((state) => state.chat || {});
  const {
    conversations = [],
    eligibleUsers = [],
    messages = {},      // { [conversationId]: [...] }
    onlineUsers = [],
    loading = false,
    error = null,
  } = chatState;

  // Connect socket + listeners
  useEffect(() => {
    if (!open || !currentUserId) return;

    const token = getUserToken();
    if (!token) {
      console.warn("⚠️ No valid token found for socket connection");
      return;
    }
    connectWithToken(token);

    const handleReceive = (msg) => {
      // Decrypt message content
      const decryptedText = decrypt(msg.message || msg.text || msg.encryptedText);
      const decryptedMsg = {
        ...msg,
        message: decryptedText,
        text: decryptedText
      };

      dispatch(
        addMessageToConversation({
          conversationId: msg.conversationId,
          message: decryptedMsg,
        })
      );

      // Increment unread count if chat is closed or viewing different conversation
      if (!open || selectedConversationId !== msg.conversationId) {
        dispatch(incrementUnreadCount());
      }
    };

    const handleDenied = (data) => {
      // alert(data.reason);
    };

    const handleMessageError = (data) => {
      console.error("Message error:", data.error);
    };

    const handleStatusChange = (data) => {
      dispatch(updateOnlineStatus(data));
    };

    const handleInitialList = (list) => {
      dispatch(setOnlineUsers(list));
    };

    socket.on("receiveMessage", handleReceive);
    socket.on("chat_denied", handleDenied);
    socket.on("messageError", handleMessageError);
    socket.on("user_status_change", handleStatusChange);
    socket.on("initial_online_list", handleInitialList);

    return () => {
      socket.off("receiveMessage", handleReceive);
      socket.off("chat_denied", handleDenied);
      socket.off("messageError", handleMessageError);
      socket.off("user_status_change", handleStatusChange);
      socket.off("initial_online_list", handleInitialList);
    };
  }, [open, currentUserId, dispatch, selectedConversationId]);

  // Reset unread count when chat is opened and conversation is selected
  useEffect(() => {
    if (open && selectedConversationId) {
      dispatch(resetUnreadCount());
      // Also mark as read in backend
      dispatch(markAsRead({ conversationId: selectedConversationId, userId: currentUserId }));
    }
  }, [open, selectedConversationId, dispatch, currentUserId]);

  // Load conversations & eligible users when popup opens
  useEffect(() => {
    if (open && currentUserId) {
      dispatch(fetchConversations(currentUserId));
      dispatch(fetchEligibleChatUsers(currentUserId));
    }
  }, [open, currentUserId, dispatch]);

  const handleSelectUser = useCallback(
    async (user) => {
      try {
        const result = await dispatch(
          getOrCreateConversation({
            user1Id: currentUserId,
            user2Id: user.userId,
          })
        ).unwrap();

        console.log("Got message:", result);
        const conversationId = result.conversationId;  // ✅ now directly on object
        if (!conversationId) throw new Error("No conversation found");

        setSelectedUser(user);
        setSelectedConversationId(conversationId);      // ✅ set to 1

        socket.emit("joinConversation", {
          conversationId,                              // ✅ use 1
          targetUserId: user.userId,
        });

        // ✅ Automatically switch to Chats tab
        setActiveTab(0);
      } catch (error) {
        console.error("Failed to start conversation:", error);
      }
    },
    [currentUserId, dispatch]
  );


  // Send message
  const sendMessage = useCallback(
    (text) => {
      if (!selectedConversationId || !text.trim() || !selectedUser) {
        return;
      }

      const trimmed = text.trim();
      const tempId = Date.now();


      // Optimistic local message
      const optimisticMessage = {
        conversation_id: selectedConversationId,
        receiverId: selectedUser.userId,
        senderId: Number(currentUserId),
        text: trimmed,
        time: new Date().toISOString(),
        tempId,
      };

      dispatch(
        addMessageToConversation({
          conversationId: selectedConversationId,
          message: optimisticMessage,
        })
      );

      // ✅ EMIT VIA SOCKET for real-time delivery
      if (socket.connected) {
        const encryptedMsg = encrypt(trimmed);
        socket.emit("sendMessage", {
          conversationId: selectedConversationId,
          message: encryptedMsg, // Encrypted for network
          text: encryptedMsg,
          receiverId: selectedUser.userId,
          senderId: Number(currentUserId),
          tempId,
        });
      } else {
        console.warn("⚠️ Socket not connected! Falling back to HTTP");
        const encryptedMsg = encrypt(trimmed);
        dispatch(
          addMessage({
            conversationId: selectedConversationId,
            message: encryptedMsg, // Encrypted for network
            receiverId: selectedUser.userId,
            senderId: Number(currentUserId),
          })
        );
      }
    },
    [selectedConversationId, selectedUser, currentUserId, dispatch]
  );

  const handleBackToUsers = () => {
    setSelectedUser(null);
    setSelectedConversationId(null);
    setActiveTab(1);
  };

  const handleBlockUser = useCallback(
    async () => {
      if (!selectedUser || !currentUserId) return;

      try {
        await dispatch(
          removeFavouration({
            userId: currentUserId,
            favId: selectedUser.userId
          })
        ).unwrap();

        // Close chat and refresh eligible users
        setSelectedUser(null);
        setSelectedConversationId(null);
        setActiveTab(1);
        dispatch(fetchEligibleChatUsers(currentUserId));

        // Optionally close the entire chat popup
        // onClose();
      } catch (error) {
        console.error("Failed to block user:", error);
      }
    },
    [selectedUser, currentUserId, dispatch, onClose]
  );

  const currentMessages = selectedConversationId
    ? messages[selectedConversationId] || []
    : [];

  // No longer returning early with a separate Drawer for loading.
  // We handle loading inside the main Drawer to avoid unmounting/remounting cycles.

  // console.log("DEBUG:", {
  //   selectedConversationId,
  //   currentMessages,
  //   messagesKeys: Object.keys(messages)
  // });

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, height: "100vh", display: "flex", flexDirection: "column" }}>
        {error && (
          <Alert
            severity="error"
            onClose={() => dispatch(clearChatError())}
            sx={{ mx: 2, mt: 1 }}
          >
            {typeof error === "string" ? error : error?.message || "Chat error"}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            bgcolor: theme.palette.primary.main,
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.7)',
              '&.Mui-selected': {
                color: '#fff'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.custom?.logoColor || theme.palette.secondary.main
            }
          }}
        >
          <Tab label={`Chats (${conversations.length})`} />
          <Tab label={`Users (${eligibleUsers.length})`} />
        </Tabs>

        {activeTab === 0 && (
          <div
            style={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {selectedUser && selectedConversationId ? (
              <>
                <ChatHeader
                  user={selectedUser}
                  onClose={handleBackToUsers}
                  isOnline={onlineUsers.includes(String(selectedUser.userId))}
                  onBlock={handleBlockUser}
                />
                <div style={{ flex: 1, overflow: "auto" }}>
                  <ChatMessages messages={currentMessages} userId={String(currentUserId)} />
                </div>
                <ChatInput onSend={sendMessage} />
              </>
            ) : (
              <div
                style={{
                  padding: 20,
                  textAlign: "center",
                  color: "#999",
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 16, marginBottom: 8 }}>No active chat</div>
                  <div>Switch to "Users" tab to start chatting</div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 1 && (
          <div style={{ flex: 1, overflow: "auto" }}>
            <EligibleChatUsers
              currentUserId={currentUserId}
              onSelectUser={handleSelectUser}
            />
          </div>
        )}
      </Box>
    </Drawer >
  );
};

export default ChatPopup;
